import { useI18n } from '@ecom/i18n';
import {
  EmptyState,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useAuthEventsQuery } from '../api';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';

const PERIODS = [1, 6, 24, 72, 168] as const;

/** Sign-in outcomes over a period, and where the failures come from. Identifiers are fingerprints cut to 8 characters: they correlate, they name nobody. */
export function AuthEventsPanel({ reason }: { readonly reason: string }): ReactElement {
  const { t } = useI18n();
  const [hours, setHours] = useState<number>(24);
  const query = useAuthEventsQuery(reason, hours, true);

  const grid = (
    title: string,
    rows: readonly { key: string; count: number }[],
    keyLabel: string,
  ): ReactElement => (
    <section className="flex flex-col gap-2">
      <h3 className="font-display text-base font-semibold text-ink">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-inkFaint">{t('admin.security.events.empty')}</p>
      ) : (
        <Table caption={title}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{keyLabel}</TableHeaderCell>
              <TableHeaderCell>{t('admin.security.events.colCount')}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>
                  <span className="font-mono text-xs text-ink">{row.key}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-ink">{row.count}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="max-w-48">
        <Select
          aria-label={t('admin.security.events.hours')}
          placeholder={t('admin.security.events.hours')}
          options={PERIODS.map((value) => ({
            value: String(value),
            label: t('admin.security.events.hoursOption', { hours: value }),
          }))}
          value={String(hours)}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (next > 0) setHours(next);
          }}
        />
      </div>

      {query.isPending ? (
        <TableSkeleton columns={2} />
      ) : query.isError ? (
        <EmptyState title={t('admin.security.loadError')} />
      ) : (
        <>
          {grid(
            t('admin.security.events.totals'),
            Object.entries(query.data.totals)
              .map(([key, count]) => ({ key, count }))
              .sort((a, b) => b.count - a.count),
            t('admin.security.events.colEvent'),
          )}
          {grid(
            t('admin.security.events.failedByIp'),
            query.data.failedByIp,
            t('admin.security.audit.colIp'),
          )}
          {grid(
            t('admin.security.events.failedByIdentifier'),
            query.data.failedByIdentifier,
            t('admin.security.events.colKey'),
          )}
        </>
      )}
    </div>
  );
}
